import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { FiClock, FiMail, FiUserCheck } from 'react-icons/fi';
import { userApi } from '../services/api';

function PendingApprovalPage() {
  const { user, logout, checkAuth } = useAuthStore();
  const navigate = useNavigate();
  const [timeSinceRegistration, setTimeSinceRegistration] = useState('');

  useEffect(() => {
    // Check if user is authenticated
    if (!user) {
      checkAuth();
    }
    
    // Calculate time since registration
    const calculateTimeSince = () => {
      if (user?.createdAt) {
        const now = new Date();
        const registeredAt = new Date(user.createdAt);
        const diffInHours = Math.floor((now - registeredAt) / (1000 * 60 * 60));
        
        if (diffInHours < 1) {
          setTimeSinceRegistration('Před několika minutami');
        } else if (diffInHours < 24) {
          setTimeSinceRegistration(`Před ${diffInHours} hodinami`);
        } else if (diffInHours < 48) {
          setTimeSinceRegistration('Před 1 dnem');
        } else {
          setTimeSinceRegistration(`Před ${Math.floor(diffInHours / 24)} dny`);
        }
      }
    };
    
    calculateTimeSince();
    const interval = setInterval(calculateTimeSince, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [user, checkAuth]);

  useEffect(() => {
    // Periodically check if user has been approved
    const checkApproval = async () => {
      try {
        await checkAuth();
        const { user: currentUser } = useAuthStore.getState();
        if (currentUser?.role !== 'PENDING') {
          // User has been approved, redirect to home
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking approval:', error);
      }
    };
    
    const interval = setInterval(checkApproval, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [checkAuth, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // If user is approved, redirect
  useEffect(() => {
    if (user && user.role !== 'PENDING') {
      navigate('/');
    }
  }, [user, navigate]);


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mb-4">
            <FiClock className="w-12 h-12 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Čekání na schválení
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-2xl sm:px-10">
          <div className="text-center">
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-6 rounded-xl mb-6">
              <div className="flex items-center justify-center mb-4">
                <FiClock className="text-4xl text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Váš účet čeká na schválení</h3>
              <p className="text-sm text-yellow-700">
                Administrátor byl informován o vaší registraci a brzy vaše žádost zkontroluje.
              </p>
            </div>

            <div className="space-y-4 text-left">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FiMail className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Emailová adresa</h4>
                  <p className="text-gray-600">{user?.email}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <FiUserCheck className="text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Zaregistrováno</h4>
                  <p className="text-gray-600">{timeSinceRegistration}</p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Co se stane dále?</h4>
                <ol className="text-sm text-gray-600 space-y-1">
                  <li>1. Administrátor zkontroluje vaše údaje</li>
                  <li>2. Obdržíte potvrzovací email na {user?.email}</li>
                  <li>3. Po schválení se můžete přihlásit</li>
                </ol>
              </div>
            </div>

            <div className="mt-6 flex flex-col space-y-3">
              <button
                onClick={() => checkAuth()}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Zkontrolovat stav
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Zrušit a odhlásit se
              </button>
            </div>

            <div className="mt-4 text-center text-sm text-gray-500">
              <p>
                Máte otázky? Kontaktujte prosím administrátora systému.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PendingApprovalPage;
